import { Box, Flex, MobileOnly, RichText, TabletUp, Text } from "@engaging-tech/components"
import React, { useEffect, useState } from "react"
import { useTranslation } from "react-i18next"

import { isQuestionVisible } from "../../../utils/logic"
import SurveyIcon from "../../ui/components/SurveyIcon/SurveyIcon"
import questionContainer from "../containers/question.container"
import { useSectionContext } from "../contexts/sectionContext"
import { parseRichText } from "../utils/richText"
import Answer from "./answer/Answer"
import ProgressBar from "./progressBar"
import SurveyCard from "./surveyCard"

const Question = ({
  answers,
  data,
  isFirstQuestion,
  question,
  previousAnswer,
  onAnswer,
  onRemoveAnswer,
  sectionTitle,
  sectionRichTitle,
  isLastQuestion,
  onNext,
  onPrevious
}) => {
  const { t } = useTranslation(["organisation_survey"])

  const { branchingLogics, currentSection, sectionsList } = useSectionContext()

  const [visible, setVisible] = useState(false)

  const [answer, setAnswer] = useState(previousAnswer)

  const questionBranchingLogics = branchingLogics.find(i => i.targetQuestion === question.id)
  const {
    type,
    id,
    title,
    richTitle,
    required,
    icon,
    min,
    max,
    fields,
    values,
    label,
    validation,
    allowOther
  } = question

  const parsedRichTitle = parseRichText(richTitle)

  const possibleValues = questionBranchingLogics
    ? values?.filter(i => questionBranchingLogics.targetAnswers.includes(i.id))
    : values

  const isLastSection = currentSection === sectionsList.length - 1

  function getSameBranchTreeIdsFromCurrent(currentQuestionId, branchTreeMap) {
    const treeId = branchTreeMap[currentQuestionId] ?? null

    const keys = Object.keys(branchTreeMap)
    const startIndex = keys.indexOf(currentQuestionId)
    if (startIndex === -1) return []

    if (!treeId) return [currentQuestionId]

    return keys.slice(startIndex).filter(qid => branchTreeMap[qid] === treeId)
  }

  useEffect(() => {
    setAnswer(previousAnswer)
  }, [id, previousAnswer])

  useEffect(() => {
    const clearSkippedAnswer = () => {
      const foundAnswer = answers?.filter(a => a.questionId === id)
      if (foundAnswer.length) onRemoveAnswer(id)

      return null
    }

    const show = isQuestionVisible(id, answers, data?.template.questionLogics, setAnswer)

    if (show) setVisible(true)
    else {
      clearSkippedAnswer()
      onNext(true)
    }
  }, [answers, id, onNext, question, data.template.questionLogics])

  const handleAnswer = value => {
    const answerValue = type === "checkbox" ? value : [value]
    setAnswer({
      id,
      value: answerValue
    })
  }

  function arraysEqual(a, b) {
    return a.length === b.length && a.every((value, index) => value === b[index])
  }

  const validateCheckboxAnswer = () => {
    if (!answer || !answer?.value) {
      if (!required) {
        return { isValid: true, canProceed: true }
      }
      return { isValid: false, canProceed: false }
    }

    const selectedIds =
      answer?.value && !Array.isArray(answer.value)
        ? answer.value.values || []
        : Array.isArray(answer?.value)
        ? answer.value
        : answer?.value != null
        ? [answer.value]
        : []

    const otherValues = Array.isArray(answer.value) ? [] : answer.value?.otherValues || []
    const validOtherValues = otherValues.filter(ov => {
      if (!ov.value || typeof ov.value !== "string") return false
      const trimmedValue = ov.value.trim()
      if (trimmedValue === "") return false
      const otherLimit = validation?.limit ?? 250
      const otherLimitMin = 1
      return trimmedValue.length >= otherLimitMin && trimmedValue.length <= otherLimit
    })

    const otherOptionId =
      allowOther && values
        ? values.find(
            val =>
              val.value?.toLowerCase().includes("other") ||
              val.value?.toLowerCase() === "other" ||
              val.id?.toLowerCase().includes("other")
          )?.id || "other"
        : null

    if (allowOther && otherOptionId && selectedIds.includes(otherOptionId)) {
      const hasValidOtherText = validOtherValues.some(vov => vov.optionId === otherOptionId)
      if (!hasValidOtherText) {
        return { isValid: false, canProceed: false }
      }
    }

    const otherOptionIdsWithValidText = new Set(validOtherValues.map(vov => vov.optionId))
    const validSelectedIds = selectedIds.filter(_id => {
      if (otherOptionId && _id === otherOptionId) {
        return otherOptionIdsWithValidText.has(_id)
      }
      return true
    })

    const selectedCount = validSelectedIds.length
    const isEmptyAnswer = selectedCount === 0

    if (!required && !validation) {
      return { isValid: true, canProceed: true }
    }

    if (!required && validation) {
      if (isEmptyAnswer) {
        return { isValid: true, canProceed: true }
      }
      if (validation.exact != null) {
        return {
          isValid: selectedCount === validation.exact,
          canProceed: selectedCount === validation.exact
        }
      }
      if (validation.min != null && selectedCount < validation.min) {
        return { isValid: false, canProceed: false }
      }
      if (validation.max != null && selectedCount > validation.max) {
        return { isValid: false, canProceed: false }
      }
      return { isValid: true, canProceed: true }
    }

    if (required && validation) {
      if (isEmptyAnswer) {
        return { isValid: false, canProceed: false }
      }
      if (validation.exact != null) {
        return {
          isValid: selectedCount === validation.exact,
          canProceed: selectedCount === validation.exact
        }
      }
      if (validation.min != null && selectedCount < validation.min) {
        return { isValid: false, canProceed: false }
      }
      if (validation.max != null && selectedCount > validation.max) {
        return { isValid: false, canProceed: false }
      }
      return { isValid: true, canProceed: true }
    }

    if (required && !validation) {
      return {
        isValid: !isEmptyAnswer,
        canProceed: !isEmptyAnswer
      }
    }

    return { isValid: true, canProceed: true }
  }

  const handleSubmit = () => {
    if (type === "checkbox") {
      const validationResult = validateCheckboxAnswer()
      if (!validationResult.canProceed) {
        return
      }

      if (!answer || !answer?.value) {
        if (!required) {
          onNext(undefined)
          return
        }
        return
      }
    } else if (!answer || !answer?.value) {
      if (!required) {
        onNext(undefined)
        return
      }
      return
    }

    const selectedIds =
      type === "checkbox" && answer?.value && !Array.isArray(answer.value)
        ? answer.value.values || []
        : Array.isArray(answer?.value)
        ? answer.value
        : answer?.value != null
        ? [answer.value]
        : []
    const prevIds =
      type === "checkbox" && previousAnswer?.value && !Array.isArray(previousAnswer.value)
        ? previousAnswer.value.values || []
        : Array.isArray(previousAnswer?.value)
        ? previousAnswer.value
        : previousAnswer?.value != null
        ? [previousAnswer.value]
        : []

    const branchingLogic =
      type === "checkbox"
        ? data?.template?.branchingLogic.find(
            i => i.currentQuestion === question.id && selectedIds.includes(i.choice)
          )
        : data?.template?.branchingLogic.find(
            i => i.currentQuestion === question.id && i.choice === answer?.value?.[0]
          )

    if (
      branchingLogic &&
      previousAnswer !== null &&
      answer?.id &&
      previousAnswer?.id === answer.id &&
      !arraysEqual(prevIds, selectedIds)
    ) {
      const clearQuestionIds = getSameBranchTreeIdsFromCurrent(question?.id, data?.branchTreeMap)
      onRemoveAnswer(clearQuestionIds)
    }

    onAnswer(answer)
    onNext(branchingLogic)
  }

  const handlePreviousQuestion = () => {
    onPrevious(question.id)
  }

  const getValidationMessage = () => {
    if (type !== "checkbox") return null
    if (!validation) return null

    if (validation.exact != null) {
      return `Must select exactly ${validation.exact} ${validation.exact === 1 ? "option" : "options"}`
    }

    if (validation.min != null) {
      return `Must select at least ${validation.min} ${validation.min === 1 ? "option" : "options"}`
    }

    if (validation.max != null) {
      return `Must select at most ${validation.max} ${validation.max === 1 ? "option" : "options"}`
    }

    return null
  }

  const validationMessage = getValidationMessage()

  const validChecker = () => {
    if (type === "checkbox") {
      const validationResult = validateCheckboxAnswer()
      return validationResult.isValid
    }

    if (!required) {
      if (!answer || !answer?.value) {
        return true
      }
      if (!answer?.id) return false

      switch (type) {
        case "scale":
          if (!Array.isArray(answer.value) || answer.value.length === 0) return true
          return answer.value.every(value => typeof value === "number")
        case "short-text": {
          if (!Array.isArray(answer.value) || answer.value.length === 0) return true
          const validFields = answer.value[0]?.filter(input => !!input)
          return validFields ? validFields.length >= 0 : true
        }
        default:
          if (!Array.isArray(answer.value) || answer.value.length === 0) return true
          return answer.value.every(value => value)
      }
    }

    if (!answer || !answer?.id) return false

    switch (type) {
      case "scale":
        return answer.value?.every(value => typeof value === "number")
      case "short-text": {
        const validFields = answer.value?.[0]?.filter(input => !!input)
        return !!validFields.length
      }
      case "checkbox": {
        const checkboxValue = answer.value
        const selectedIds = checkboxValue?.values || (Array.isArray(checkboxValue) ? checkboxValue : [])
        const otherValues = checkboxValue?.otherValues || []

        const validOtherValues = otherValues.filter(ov => ov.value && ov.value.trim() !== "")
        const selectedCount = selectedIds.length + validOtherValues.length

        if (validation) {
          if (validation.min !== undefined && selectedCount < validation.min) return false
          if (validation.max !== undefined && selectedCount > validation.max) return false
          if (validation.exact !== undefined && selectedCount !== validation.exact) return false
        }
        return selectedCount > 0
      }
      default:
        return answer.value?.every(value => value)
    }
  }

  if (!visible) return null

  return (
    <SurveyCard
      isLastQuestion={isLastQuestion}
      title={sectionTitle}
      richTitle={sectionRichTitle}
      onNext={handleSubmit}
      onPrevious={handlePreviousQuestion}
      type={type}
      answer={answer}
      isValid={validChecker()}
      isFirstQuestion={isFirstQuestion}
      isLastSection={isLastSection}
    >
      <Flex>
        <TabletUp>
          <SurveyIcon name={icon} mr={3} />
        </TabletUp>
        <Box>
          <MobileOnly>
            <Flex mb={3} alignItems="flex-end">
              <SurveyIcon name={icon} mr={3} />
              <ProgressBar id={id} />
            </Flex>
          </MobileOnly>

          <TabletUp>
            <ProgressBar id={id} />
          </TabletUp>

          {parsedRichTitle ? (
            <Box fontWeight="500" color="dark.2" fontSize={[3, 4]}>
              <RichText value={parsedRichTitle} openLinksInNewTab />
            </Box>
          ) : (
            <Text fontWeight="500" color="dark.2" fontSize={[3, 4]}>
              {title}
            </Text>
          )}

          {!required && (
            <Text fontSize={3} color="dark.2">
              {" "}
              ({t("questions.optional", "")})
            </Text>
          )}

          {validationMessage && (
            <Text color="dark.2" fontSize={1} mt={2} mb={2} display="block">
              {validationMessage}
            </Text>
          )}

          <Answer
            type={type}
            fields={fields}
            values={possibleValues}
            min={min}
            max={max}
            label={label}
            validation={validation}
            allowOther={allowOther}
            onAnswer={value => handleAnswer(value)}
            initialValue={answer}
            allAnswers={answers}
            choiceLogics={data.template.choiceLogics}
          />
        </Box>
      </Flex>
    </SurveyCard>
  )
}

export default questionContainer(Question)
